import { DraggableAttributes } from "@dnd-kit/core";
import { cva } from "class-variance-authority";
import * as React from "react";

import { faGripLines } from "@performyard/fontawesome/pro-regular-svg-icons";

import { cn } from "../../../lib/utils";

import { Icon } from "../../atoms/Icon";
import { Text } from "../../atoms/Text";
import { Chip } from "../Chip";
import SortableDragNDrop, { type DragHandlers as SortableDragHandlers } from "../SortableDragNDrop";

const orderedItemRowVariants = cva("flex items-center py-0.5 transition-all", {
	variants: {
		dragging: {
			true: "z-50 opacity-50",
		},
		disabled: {
			true: "cursor-not-allowed opacity-disabled",
			false: "",
		},
	},
	defaultVariants: {
		dragging: false,
		disabled: false,
	},
});

type DragHandlers = {
	listeners: SortableDragHandlers["listeners"];
	attributes: DraggableAttributes;
};

type SortableOrderedListProps<T> = {
	/**
	 * Array of values to display in the sortable list. Order determines display order.
	 */
	values: T[];
	/**
	 * Optional callback function called when items are reordered or removed.
	 * @param newValues - The new array of values in their current order
	 */
	onChange?: (newValues: T[]) => void;
	/**
	 * Whether the list is disabled (hides drag handle, disables drag and drop, and applies disabled opacity).
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * Whether the list is read-only (hides drag handle and disables drag and drop, but does not change opacity).
	 * @default false
	 */
	readOnly?: boolean;
	/**
	 * Custom render function for displaying items in the list.
	 * @param value - The value to render
	 * @param index - The 1-based index of this item (display number)
	 * @param onRemove - Callback function to remove this item
	 * @param dragHandlers - Drag and drop handlers with listeners and attributes for the drag handle
	 * @param isDragging - Whether this item is currently being dragged
	 * @returns React node to render for the item
	 */
	renderItem?: (
		value: T,
		index: number,
		onRemove: () => void,
		dragHandlers: DragHandlers,
		isDragging: boolean
	) => React.ReactNode;
	/**
	 * Function to get a label string from a value. Used for default rendering when renderItem is not provided.
	 * @param value - The value to get a label for
	 * @returns String label to display
	 */
	getLabel?: (value: T) => string;
	/**
	 * Function to get a unique ID from a value. Used for drag and drop identification.
	 * @param value - The value to get an ID for
	 * @returns String ID
	 */
	getItemId?: (value: T) => string;
	/**
	 * Optional CSS classes to apply to the component container (use with caution).
	 */
	dangerouslySetClassName?: string;
	/**
	 * Test identifier for the list container (used for testing).
	 */
	"data-testid"?: string;
};

const SortableOrderedList = <T,>({
	values,
	onChange,
	disabled = false,
	readOnly = false,
	renderItem,
	getLabel,
	getItemId,
	dangerouslySetClassName,
	"data-testid": dataTestId,
}: SortableOrderedListProps<T>) => {
	const defaultGetItemId = React.useCallback(
		(val: T): string => {
			if (getItemId) {
				return getItemId(val);
			}
			if (typeof val === "object" && val !== null && "id" in val) {
				return String((val as { id: unknown }).id);
			}
			return typeof val === "string" ? val : JSON.stringify(val);
		},
		[getItemId]
	);

	// Transform values into items with _id for SortableDragNDrop
	const items = React.useMemo(
		() => values.map((value) => ({ _id: defaultGetItemId(value), value })),
		[values, defaultGetItemId]
	);

	const handleRemove = (index: number) => {
		return () => {
			if (!disabled && !readOnly && onChange) {
				const newValues = values.filter((_, i) => i !== index);
				onChange(newValues);
			}
		};
	};

	const handleChange = (newItems: typeof items) => {
		if (onChange) {
			// Extract the original values from items
			onChange(newItems.map((item) => item.value));
		}
	};

	if (values.length === 0) {
		return null;
	}

	return (
		<SortableDragNDrop
			items={items}
			onChange={handleChange}
			disabled={disabled || readOnly}
			strategy="verticalList"
			restrictToVerticalAxis
			restrictToParentElement
			showDragOverlay={false}
			useUnorderedList={false}
			listClassName={cn("flex w-full flex-col", dangerouslySetClassName)}
			listAriaLabel="Sortable list"
			data-testid={dataTestId || "sortable-ordered-list"}
			renderItem={(item, { isDragging, dragHandlers }) => {
				const index = items.findIndex((i) => i._id === item._id);
				const value = item.value;

				// Adapt SortableDragNDrop's dragHandlers to match SortableOrderedList's DragHandlers type
				const adaptedDragHandlers: DragHandlers = {
					listeners: dragHandlers.listeners,
					attributes: dragHandlers.attributes,
				};

				return (
					<div aria-label={getLabel ? getLabel(value) : `Item ${index + 1}`}>
						{renderItem ? (
							renderItem(value, index + 1, handleRemove(index), adaptedDragHandlers, isDragging)
						) : (
							<div
								className={cn(
									orderedItemRowVariants({
										dragging: isDragging,
										disabled: disabled,
									})
								)}
								data-testid={`sortable-item-${index}`}
							>
								{!disabled && !readOnly && (
									<div
										{...dragHandlers.attributes}
										{...dragHandlers.listeners}
										className="flex shrink-0 items-center"
										role="button"
										aria-label={`Drag to reorder item ${index + 1}`}
										tabIndex={0}
									>
										<Icon
											icon={faGripLines}
											size={24}
											color="neutral-7"
											dangerouslySetClassName="cursor-move"
											aria-hidden="true"
										/>
									</div>
								)}
								<Text
									size="caption"
									weight="regular"
									color="neutral-9"
									dangerouslySetClassName="shrink-0 px-1"
								>
									{index + 1}.
								</Text>
								<div className="min-w-0 flex-1">
									<Chip
										label={getLabel ? getLabel(value) : String(value)}
										color="primary"
										size={24}
										onDismiss={disabled || readOnly ? undefined : handleRemove(index)}
										dangerouslySetClassName="max-w-full"
									/>
								</div>
							</div>
						)}
					</div>
				);
			}}
		/>
	);
};

SortableOrderedList.displayName = "SortableOrderedList";

export { SortableOrderedList, type SortableOrderedListProps, type DragHandlers };
